import { useCallback, useRef, useState } from 'react';

import { buildLineItemsBookingData } from './BookingPanel.helpers';

/**
 * @typedef {Object} BookingPreviewData
 * @property {Array} bookingDates - [startDate, endDate]; either may be null while incomplete
 * @property {Object} guestCount
 * @property {Object} [extraLineItems]
 * @property {Object} [customLineItems]
 */

const hasCompleteRange = ({ bookingDates }) => Boolean(bookingDates[0] && bookingDates[1]);

/**
 * Voucher-aware price preview shared by both booking panel layouts. Remembers the last
 * booking data so applying a voucher re-fetches with the current extras and custom
 * items (otherwise discounts would be computed against night + base commission only).
 *
 * @param {Object} params
 * @param {Object} params.listingId - SDK UUID of the listing
 * @param {boolean} [params.isOwnListing]
 * @param {Function} [params.onFetchTransactionLineItems]
 * @returns {{ onBookingDataChange: (data: BookingPreviewData) => void, onApplyVoucher: (code: string) => void }}
 */
const useBookingPreview = ({ listingId, isOwnListing = false, onFetchTransactionLineItems }) => {
  const [voucherCode, setVoucherCode] = useState(null);
  const lastBookingDataRef = useRef({ bookingDates: [null, null], guestCount: {} });

  const fetchLineItems = useCallback(
    (bookingValues) => {
      if (hasCompleteRange(bookingValues) && onFetchTransactionLineItems) {
        onFetchTransactionLineItems({
          bookingData: buildLineItemsBookingData(bookingValues),
          listingId,
          isOwnListing,
        });
      }
    },
    [onFetchTransactionLineItems, listingId, isOwnListing],
  );

  const onBookingDataChange = useCallback(
    ({ bookingDates, guestCount, extraLineItems, customLineItems }) => {
      lastBookingDataRef.current = { bookingDates, guestCount, extraLineItems, customLineItems };
      const voucher = voucherCode ? { code: voucherCode } : null;
      fetchLineItems({ bookingDates, guestCount, voucher, extraLineItems, customLineItems });
    },
    [fetchLineItems, voucherCode],
  );

  const onApplyVoucher = useCallback(
    (code) => {
      setVoucherCode(code);
      fetchLineItems({ ...lastBookingDataRef.current, voucher: { code } });
    },
    [fetchLineItems],
  );

  return { onBookingDataChange, onApplyVoucher };
};

export default useBookingPreview;
